import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { FiMessageSquare, FiSend, FiUsers } from 'react-icons/fi';
import { getDiscussions, getDiscussionMessages, sendDiscussionMessage, getEmployes } from '../utils/api';
import './Discussions.css';

const Discussions = () => {
  const [discussions, setDiscussions] = useState([]);
  const [selectedDiscussion, setSelectedDiscussion] = useState(null);
  const [messages, setMessages] = useState([]);
  const [employes, setEmployes] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [shouldAutoScroll, setShouldAutoScroll] = useState(true);

  useEffect(() => {
    loadData();
    const interval = setInterval(() => {
      if (selectedDiscussion) {
        // Charger les messages sans forcer le scroll
        loadMessagesSilently(selectedDiscussion.id);
      }
    }, 3000); // Rafraîchir toutes les 3 secondes pour voir les nouvelles réponses
    return () => clearInterval(interval);
  }, [selectedDiscussion]);

  const loadData = async () => {
    try {
      const [discussionsRes, employesRes] = await Promise.all([
        getDiscussions(),
        getEmployes()
      ]);
      setDiscussions(discussionsRes.data);
      const allEmployes = employesRes.data || [];
      setEmployes(allEmployes);
      
      // Vérifier si l'employé de l'utilisateur existe
      const userStr = localStorage.getItem('user');
      if (userStr) {
        try {
          const user = JSON.parse(userStr);
          const userEmploye = allEmployes.find(emp => 
            (emp.email && user.email && emp.email.toLowerCase() === user.email.toLowerCase()) || 
            (emp.nom && emp.prenom && user.nom && user.prenom && 
             emp.nom.toLowerCase() === user.nom.toLowerCase() && 
             emp.prenom.toLowerCase() === user.prenom.toLowerCase())
          );
          
          if (!userEmploye) {
            console.warn('Employé de l\'utilisateur non trouvé dans la liste. Les employés disponibles:', allEmployes);
          }
        } catch (e) {
          console.error('Erreur parsing user:', e);
        }
      }
      
      if (discussionsRes.data.length > 0 && !selectedDiscussion) {
        setSelectedDiscussion(discussionsRes.data[0]);
        loadMessages(discussionsRes.data[0].id);
      }
      setLoading(false);
    } catch (error) {
      console.error('Erreur lors du chargement:', error);
      setLoading(false);
    }
  };

  const loadMessages = async (discussionId, forceScroll = false) => {
    try {
      const container = document.querySelector('.messages-container');
      const wasNearBottom = container ? 
        (container.scrollHeight - container.scrollTop - container.clientHeight < 100) : true;
      
      const response = await getDiscussionMessages(discussionId);
      setMessages(response.data);
      
      // Scroller vers le bas seulement si l'utilisateur était déjà en bas OU si on force le scroll
      setTimeout(() => {
        const container = document.querySelector('.messages-container');
        if (container && (forceScroll || (wasNearBottom && shouldAutoScroll))) {
          container.scrollTop = container.scrollHeight;
          setShouldAutoScroll(true);
        }
      }, 100);
    } catch (error) {
      console.error('Erreur lors du chargement des messages:', error);
    }
  };

  const loadMessagesSilently = async (discussionId) => {
    try {
      const container = document.querySelector('.messages-container');
      const wasNearBottom = container ? 
        (container.scrollHeight - container.scrollTop - container.clientHeight < 100) : true;
      
      const response = await getDiscussionMessages(discussionId);
      setMessages(prevMessages => {
        const oldMessagesCount = prevMessages.length;
        const hasNewMessages = response.data.length > oldMessagesCount;
        
        // Scroller vers le bas SEULEMENT si l'utilisateur était déjà en bas ET qu'il y a de nouveaux messages
        if (wasNearBottom && shouldAutoScroll && hasNewMessages) {
          setTimeout(() => {
            const container = document.querySelector('.messages-container');
            if (container) {
              container.scrollTop = container.scrollHeight;
            }
          }, 100);
        }
        
        return response.data;
      });
    } catch (error) {
      console.error('Erreur lors du chargement des messages:', error);
    }
  };

  const handleSelectDiscussion = (discussion) => {
    setSelectedDiscussion(discussion);
    loadMessages(discussion.id);
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedDiscussion) return;

    try {
      // Trouver l'employé correspondant à l'utilisateur connecté (Gauthier Thery)
      const userStr = localStorage.getItem('user');
      let employeId = null;
      
      if (userStr) {
        try {
          const user = JSON.parse(userStr);
          console.log('User from localStorage:', user);
          console.log('Available employes:', employes);
          
          // Chercher l'employé correspondant par email ou nom/prenom
          const userEmploye = employes.find(emp => 
            (emp.email && user.email && emp.email.toLowerCase() === user.email.toLowerCase()) || 
            (emp.nom && emp.prenom && user.nom && user.prenom && 
             emp.nom.toLowerCase() === user.nom.toLowerCase() && 
             emp.prenom.toLowerCase() === user.prenom.toLowerCase())
          );
          
          if (userEmploye) {
            employeId = userEmploye.id;
            console.log('Found employe:', userEmploye);
          } else {
            console.log('No matching employe found, using first one');
          }
        } catch (e) {
          console.error('Erreur parsing user:', e);
        }
      }
      
      // Si pas trouvé, essayer de recharger les employés et chercher à nouveau
      if (!employeId) {
        try {
          const employesRes = await getEmployes();
          const allEmployes = employesRes.data || [];
          setEmployes(allEmployes);
          
          if (userStr) {
            try {
              const user = JSON.parse(userStr);
              const userEmploye = allEmployes.find(emp => 
                (emp.email && user.email && emp.email.toLowerCase() === user.email.toLowerCase()) || 
                (emp.nom && emp.prenom && user.nom && user.prenom && 
                 emp.nom.toLowerCase() === user.nom.toLowerCase() && 
                 emp.prenom.toLowerCase() === user.prenom.toLowerCase())
              );
              
              if (userEmploye) {
                employeId = userEmploye.id;
                console.log('Found employe after reload:', userEmploye);
              }
            } catch (e) {
              console.error('Erreur parsing user:', e);
            }
          }
          
          // Si toujours pas trouvé, utiliser le premier employé disponible
          if (!employeId && allEmployes.length > 0) {
            employeId = allEmployes[0].id;
            console.log('Using first available employe:', allEmployes[0]);
          }
        } catch (error) {
          console.error('Erreur lors du rechargement des employés:', error);
        }
      }
      
      if (!employeId) {
        console.error('No employe found! Available employes:', employes);
        alert('Erreur: Aucun employé trouvé. Veuillez recharger la page ou contacter l\'administrateur.');
        return;
      }

      console.log('Sending message with employeId:', employeId);
      await sendDiscussionMessage(selectedDiscussion.id, employeId, newMessage);
      setNewMessage('');
      // Attendre un peu pour que le message soit sauvegardé
      setTimeout(async () => {
        setShouldAutoScroll(true); // Forcer le scroll après l'envoi d'un message
        await loadMessages(selectedDiscussion.id, true); // Force le scroll après envoi
      }, 500);
    } catch (error) {
      console.error('Erreur lors de l\'envoi:', error);
      alert('Erreur lors de l\'envoi du message: ' + (error.response?.data?.error || error.message));
    }
  };

  if (loading) {
    return <div className="loading">Chargement des discussions...</div>;
  }

  return (
    <div className="discussions-page">
      <div className="page-header">
        <h2>Discussions</h2>
        <p>Communication interne et gestion de projets</p>
      </div>

      <div className="discussions-container">
        <div className="discussions-sidebar">
          <div className="discussions-list">
            {discussions.map((discussion) => (
              <div
                key={discussion.id}
                className={`discussion-item ${selectedDiscussion?.id === discussion.id ? 'active' : ''}`}
                onClick={() => handleSelectDiscussion(discussion)}
              >
                <div className="discussion-icon">
                  {discussion.type === 'generale' ? (
                    <FiMessageSquare />
                  ) : (
                    <FiUsers />
                  )}
                </div>
                <div className="discussion-info">
                  <h4>{discussion.titre}</h4>
                  <p>{discussion.nb_messages} message(s)</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="discussions-main">
          {selectedDiscussion ? (
            <>
              <div className="discussion-header">
                <h3>{selectedDiscussion.titre}</h3>
                <span className="discussion-type">
                  {selectedDiscussion.type === 'generale' ? 'Discussion Générale' : 'Groupe de Travail'}
                </span>
              </div>

              <div 
                className="messages-container"
                onScroll={(e) => {
                  const container = e.target;
                  const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 100;
                  setShouldAutoScroll(isNearBottom);
                }}
              >
                {messages.map((message) => (
                  <div key={message.id} className="discussion-message">
                    <div className="message-avatar">
                      {message.avatar || `${message.prenom[0]}${message.nom[0]}`}
                    </div>
                    <div className="message-wrapper">
                      <div className="message-header">
                        <span className="message-author">
                          {message.prenom} {message.nom}
                        </span>
                        <span className="message-role">{message.role}</span>
                        <span className="message-time">
                          {format(new Date(message.created_at), 'dd/MM/yyyy HH:mm', { locale: fr })}
                        </span>
                      </div>
                      <div className="message-content">{message.contenu}</div>
                    </div>
                  </div>
                ))}
              </div>

              <form className="message-input-form" onSubmit={handleSendMessage}>
                <input
                  type="text"
                  placeholder="Tapez votre message..."
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  className="message-input"
                />
                <button type="submit" className="send-button">
                  <FiSend />
                </button>
              </form>
            </>
          ) : (
            <div className="no-discussion">
              <FiMessageSquare />
              <p>Sélectionnez une discussion</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Discussions;

